import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

// Actual Firebase import
import { auth, functions, storage } from '../firebase'; // Ensure this path is correct
// Import Firebase v9 modular functions directly
import { httpsCallable } from 'firebase/functions';
import { ref as firebaseStorageRef, uploadBytesResumable as firebaseUploadBytesResumable } from 'firebase/storage';


// Actual Font Awesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUsers, faCalendarAlt, faPhone, faMapMarkerAlt, faCity, faMapPin,
    faImage, faSpinner, faCheckCircle, faExclamationTriangle, faUpload, faMap
} from '@fortawesome/free-solid-svg-icons';

// Actual date-fns import
import { isValid, parse } from 'date-fns';


// Callable Function Reference for V2
// `functions` imported from '../firebase' is the Functions service instance
const addFamilyV2Callable = httpsCallable(functions, 'addFamilyV2');


// Helper to get months for dropdown
const getMonths = () => {
    const months = [];
    for (let i = 0; i < 12; i++) {
        months.push({ value: i + 1, label: new Date(0, i).toLocaleString('default', { month: 'long' }) });
    }
    return months;
};

// Helper to get days in month for dropdown
const getDaysInMonth = (month, year) => {
    if (!month) return Array.from({ length: 31 }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
    const yearToUse = (year && year >= 1800) ? parseInt(year, 10) : 2000;
    // Month for Date object is 0-indexed, so subtract 1 from the 1-indexed month value
    const days = new Date(yearToUse, parseInt(month, 10), 0).getDate();
    return Array.from({ length: days }, (_, i) => ({ value: i + 1, label: (i + 1).toString() }));
};

// Helper to get years for dropdown
const getYears = () => {
    const years = [{ value: '', label: 'Year (Optional)' }];
    const currentYear = new Date().getFullYear();
    for (let i = currentYear; i >= currentYear - 100; i--) {
        years.push({ value: i, label: i.toString() });
    }
    return years;
};


function AddFamilyPage() {
    // Form State
    const [surname, setSurname] = useState('');
    const [selectedAnniversaryMonth, setSelectedAnniversaryMonth] = useState('');
    const [selectedAnniversaryDay, setSelectedAnniversaryDay] = useState('');
    const [selectedAnniversaryYear, setSelectedAnniversaryYear] = useState('');

    const [phone, setPhone] = useState('');
    const [street, setStreet] = useState('');
    const [city, setCity] = useState('');
    const [state, setState] = useState('');
    const [zipcode, setZipcode] = useState('');

    // Image State
    const [imageFile, setImageFile] = useState(null);
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
    const [uploadProgress, setUploadProgress] = useState(0);

    // Component State
    const [isLoading, setIsLoading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [currentUserOrgId, setCurrentUserOrgId] = useState(null);

    const navigate = useNavigate(); // Assuming react-router-dom v6 for useNavigate
    const fileInputRef = useRef(null);

    // Effect to get User's Org ID
    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged(user => {
            if (user) {
                user.getIdTokenResult()
                    .then(idTokenResult => {
                        const orgId = idTokenResult.claims.orgId;
                        if (!orgId) {
                            setMessage({ type: 'danger', text: 'Error: Organization ID not found for your account. Please re-login.' });
                            console.error("User is missing orgId claim.");
                        }
                        setCurrentUserOrgId(orgId);
                    })
                    .catch(err => {
                        console.error("Error fetching user claims:", err);
                        setMessage({ type: 'danger', text: 'Error fetching user data. Please try refreshing.' });
                    });
            } else {
                setMessage({ type: 'warning', text: 'Please log in to add a family.' });
                setCurrentUserOrgId(null);
            }
        });
        return () => unsubscribe();
    }, []);

    // Effect to cleanup image preview URL
    useEffect(() => {
        return () => {
            if (imagePreviewUrl) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
        };
    }, [imagePreviewUrl]);


    const handleFileChange = (event) => {
        setMessage({ type: '', text: '' });
        const file = event.target.files[0];
        if (file && file.type.startsWith('image/')) {
            setImageFile(file);
            const newPreviewUrl = URL.createObjectURL(file);
            if (imagePreviewUrl) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
            setImagePreviewUrl(newPreviewUrl);
        } else {
            setImageFile(null);
            if (imagePreviewUrl) {
                URL.revokeObjectURL(imagePreviewUrl);
            }
            setImagePreviewUrl(null);
            if (file) {
                setMessage({ type: 'warning', text: 'Please select a valid image file.' });
            }
        }
        if (event.target) {
            event.target.value = null;
        }
    };

    const validateForm = () => {
        if (!surname.trim()) {
            setMessage({ type: 'warning', text: 'Family Surname is required.' }); return false;
        }
        const phoneDigits = phone.trim().replace(/\D/g, "");
        if (phone.trim() && (phoneDigits.length < 7 || phoneDigits.length > 15) ) {
             setMessage({ type: 'warning', text: 'Phone number, if provided, must contain between 7 and 15 digits.' }); return false;
        }
        if (state.trim() && !/^[A-Za-z]{2}$/.test(state.trim())) {
             setMessage({ type: 'warning', text: 'State, if provided, must be a 2-letter abbreviation (e.g., NC).' }); return false;
        }
        if (zipcode.trim() && !/^\d{5}(-\d{4})?$/.test(zipcode.trim())) {
             setMessage({ type: 'warning', text: 'Zipcode, if provided, must be a valid 5-digit or 9-digit format (e.g., 12345 or 12345-6789).' }); return false;
        }

        const month = selectedAnniversaryMonth;
        const day = selectedAnniversaryDay;
        const year = selectedAnniversaryYear;

        if ((month && !day) || (!month && day && month !== "")) {
            setMessage({ type: 'warning', text: 'If selecting an anniversary month or day, both are required.' }); return false;
        }
        if (year && (!month || !day)) {
            setMessage({ type: 'warning', text: 'If selecting an anniversary year, month and day are also required.' }); return false;
        }

        if (month && day) {
            const yearForValidation = year || 2000;
            const dateStr = `${yearForValidation}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
            if (!isValid(parse(dateStr, 'yyyy-MM-dd', new Date()))) {
                 setMessage({ type: 'warning', text: `Anniversary date ${month}/${day}${year ? '/'+year : ''} is not a valid calendar date.` }); return false;
            }
        }
        return true;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setMessage({ type: '', text: '' });

        if (!validateForm()) {
            return;
        }

        if (!currentUserOrgId) {
            setMessage({ type: 'danger', text: 'Cannot add family: User Organization ID is missing. Please re-login.' }); return;
        }

        setIsLoading(true);
        setUploadProgress(0);
        let gcsPhotoPath = null;

        try {
            if (imageFile) {
                setMessage({ type: 'info', text: 'Uploading photo...' });
                const fileExtension = imageFile.name.split('.').pop();
                const fileName = `${currentUserOrgId}/photos/${uuidv4()}.${fileExtension}`;

                const actualStorageRef = firebaseStorageRef(storage, fileName);
                const uploadTask = firebaseUploadBytesResumable(actualStorageRef, imageFile);

                gcsPhotoPath = await new Promise((resolve, reject) => {
                    uploadTask.on('state_changed',
                        (snapshot) => {
                            const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
                            setUploadProgress(progress);
                        },
                        (error) => {
                            console.error("Photo upload error:", error);
                            reject(new Error(`Photo upload failed: ${error.message}`));
                        },
                        () => {
                            resolve(fileName);
                        }
                    );
                });
                setMessage({ type: 'info', text: 'Photo uploaded. Adding family data...' });
            } else {
                 setMessage({ type: 'info', text: 'Processing family data...' });
            }

            const familyData = {
                surname: surname.trim(),
                street: street.trim() || null,
                city: city.trim() || null,
                state: state.trim().toUpperCase() || null,
                zipcode: zipcode.trim() || null,
                phone: phone.trim() || null,
                anniversaryMonth: selectedAnniversaryMonth ? String(selectedAnniversaryMonth) : null,
                anniversaryDay: selectedAnniversaryDay ? String(selectedAnniversaryDay) : null,
                anniversaryYear: selectedAnniversaryYear ? String(selectedAnniversaryYear) : null,
                photo: gcsPhotoPath,
            };

            console.log("Calling addFamilyV2 with data:", JSON.stringify(familyData, null, 2));
            const result = await addFamilyV2Callable(familyData);

            // --- MODIFICATION START ---
            if (result?.data?.success && result.data.familyId) {
                setMessage({ type: 'success', text: (result.data.message || 'Family added successfully! Redirecting...') });
                const newFamilyId = result.data.familyId;
                setTimeout(() => {
                    if (typeof navigate === 'function') {
                        navigate(`/families/${newFamilyId}`); // Navigate to the new family's detail page
                    }
                }, 2000);
            } else if (result?.data?.success) {
                // Successfully created but no familyId returned (fallback or error in function response)
                setMessage({ type: 'success', text: (result.data.message || 'Family added successfully! Could not get family ID, redirecting to families list.') });
                console.warn("Family created, but no familyId returned from the backend. Navigating to /families as a fallback.");
                setTimeout(() => {
                    if (typeof navigate === 'function') {
                        navigate('/families'); // Fallback to the list of all families
                    }
                }, 2000);
            }
            // --- MODIFICATION END ---
            else {
                // This covers result being undefined, or result.data.success being false
                throw new Error(result?.data?.message || 'Failed to add family due to a server issue.');
            }
        } catch (error) {
            console.error("Error submitting family:", error);
            let displayErrorMessage;
            // Assuming error from httpsCallable might have code and message directly
            if (error.code && error.message && typeof error.details !== 'undefined') {
                displayErrorMessage = error.message; // Firebase Functions error
            } else if (error.message) {
                displayErrorMessage = error.message; // Generic JavaScript error
            } else {
                displayErrorMessage = 'An unexpected error occurred. Please try again.';
            }
            setMessage({ type: 'danger', text: displayErrorMessage });
        } finally {
            setIsLoading(false);
        }
    };

    const availableMonths = React.useMemo(() => getMonths(), []);
    const availableDays = React.useMemo(() => getDaysInMonth(selectedAnniversaryMonth, selectedAnniversaryYear), [selectedAnniversaryMonth, selectedAnniversaryYear]);
    const availableYears = React.useMemo(() => getYears(), []);

    return (
        <div className="container mt-4 mb-5">
            <nav aria-label="breadcrumb" className="mb-3">
                 <ol className="breadcrumb bg-light px-2 py-1 rounded-1 small">
                     <li className="breadcrumb-item"><Link to="/dashboard">Dashboard</Link></li>
                     <li className="breadcrumb-item"><Link to="/families">Families</Link></li>
                     <li className="breadcrumb-item active" aria-current="page">Add New Family</li>
                 </ol>
            </nav>

            <div className="row justify-content-center">
                <div className="col-md-10 col-lg-8">
                    <div className="card shadow-sm">
                        <div className="card-body p-4">
                            <h2 className="card-title text-center mb-4">Add New Family</h2>

                            <form onSubmit={handleSubmit}>
                                {message.text && (
                                    <div className={`alert alert-${message.type || 'info'} d-flex flex-column`} role="alert">
                                        <div className="d-flex align-items-center">
                                            {message.type === 'success' && <FontAwesomeIcon icon={faCheckCircle} className="me-2 flex-shrink-0" />}
                                            {(message.type === 'danger' || message.type === 'warning') && <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 flex-shrink-0" />}
                                            {message.type === 'info' && isLoading && <FontAwesomeIcon icon={faSpinner} spin className="me-2 flex-shrink-0" /> }
                                            {/* Corrected this line based on likely intent: spinner when info and not loading might be if message persists post-loading */}
                                            {message.type === 'info' && !isLoading && message.text.includes("Processing") && <FontAwesomeIcon icon={faSpinner} spin className="me-2 flex-shrink-0" /> }
                                            <span>{message.text}</span>
                                        </div>
                                        {isLoading && uploadProgress > 0 && imageFile && message.type === 'info' && (
                                            <div className="progress mt-2" style={{height: '5px', width: '100%'}}>
                                                <div className="progress-bar progress-bar-striped progress-bar-animated" role="progressbar" style={{ width: `${uploadProgress}%` }} aria-valuenow={uploadProgress} aria-valuemin="0" aria-valuemax="100"></div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                <div className="mb-3">
                                    <label htmlFor="surname" className="form-label">Family Surname *</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><FontAwesomeIcon icon={faUsers} /></span>
                                        <input type="text" className="form-control" id="surname" value={surname} onChange={(e) => setSurname(e.target.value)} disabled={isLoading} />
                                    </div>
                                </div>


                                <div className="mb-3">
                                    <label htmlFor="phone" className="form-label">Home Phone</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><FontAwesomeIcon icon={faPhone} /></span>
                                        <input type="tel" className="form-control" id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={isLoading} placeholder="(Optional)"/>
                                    </div>
                                </div>

                                <div className="mb-3">
                                    <label htmlFor="street" className="form-label">Street Address</label>
                                    <div className="input-group">
                                        <span className="input-group-text"><FontAwesomeIcon icon={faMapMarkerAlt} /></span>
                                        <input type="text" className="form-control" id="street" value={street} onChange={(e) => setStreet(e.target.value)} disabled={isLoading} placeholder="(Optional)"/>
                                    </div>
                                </div>
                                <div className="row mb-3">
                                    <div className="col-md-6">
                                        <label htmlFor="city" className="form-label">City</label>
                                         <div className="input-group">
                                            <span className="input-group-text"><FontAwesomeIcon icon={faCity} /></span>
                                            <input type="text" className="form-control" id="city" value={city} onChange={(e) => setCity(e.target.value)} disabled={isLoading} placeholder="(Optional)"/>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label htmlFor="state" className="form-label">State</label>
                                         <div className="input-group">
                                            <span className="input-group-text"><FontAwesomeIcon icon={faMap} /></span>
                                            <input type="text" className="form-control text-uppercase" id="state" placeholder="e.g. NC" value={state} onChange={(e) => setState(e.target.value)} maxLength="2" disabled={isLoading} />
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <label htmlFor="zipcode" className="form-label">Zip Code</label>
                                         <div className="input-group">
                                            <span className="input-group-text"><FontAwesomeIcon icon={faMapPin} /></span>
                                            <input type="text" className="form-control" id="zipcode" value={zipcode} onChange={(e) => setZipcode(e.target.value)} disabled={isLoading} placeholder="e.g. 12345"/>
                                        </div>
                                    </div>
                                </div>

                                {/* --- Anniversary Section --- */}
                                <div className="mb-3">
                                    <label className="form-label">Anniversary (Optional)</label>
                                    <div className="row g-2">
                                        <div className="col-sm-5">
                                            <div className="input-group">
                                                 <span className="input-group-text"><FontAwesomeIcon icon={faCalendarAlt} style={{fontSize: '0.8rem'}}/></span>
                                                <select
                                                    id="anniversaryMonth"
                                                    className="form-select"
                                                    value={selectedAnniversaryMonth}
                                                    onChange={(e) => {
                                                        setSelectedAnniversaryMonth(e.target.value);
                                                        if (!e.target.value) {
                                                            setSelectedAnniversaryDay(''); // Reset day if month is cleared
                                                        }
                                                    }}
                                                    disabled={isLoading}
                                                    aria-label="Anniversary Month"
                                                >
                                                    <option value="">Month</option>
                                                    {availableMonths.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="col-sm-3">
                                            <select
                                                id="anniversaryDay"
                                                className="form-select"
                                                value={selectedAnniversaryDay}
                                                onChange={(e) => setSelectedAnniversaryDay(e.target.value)}
                                                disabled={isLoading || !selectedAnniversaryMonth} // Disable if no month selected
                                                aria-label="Anniversary Day"
                                            >
                                                <option value="">Day</option>
                                                {availableDays.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="col-sm-4">
                                            <select
                                                id="anniversaryYear"
                                                className="form-select"
                                                value={selectedAnniversaryYear}
                                                onChange={(e) => setSelectedAnniversaryYear(e.target.value)}
                                                disabled={isLoading} // Or disable if month/day not selected: disabled={isLoading || !selectedAnniversaryMonth || !selectedAnniversaryDay}
                                                aria-label="Anniversary Year"
                                            >
                                                {availableYears.map(y => <option key={y.value} value={y.value}>{y.label}</option>)}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                                {/* --- End Anniversary Section --- */}


                                <hr className="my-4"/>
                                <div className="mb-3 text-center">
                                    <label className="form-label d-block mb-3">Family Photo (Optional)</label>
                                    {imagePreviewUrl && (
                                        <div className="mb-3">
                                            <img src={imagePreviewUrl} alt="Selected family preview" style={{ maxHeight: '200px', maxWidth: '90%', height: 'auto', border: '1px solid #ccc', padding: '4px', borderRadius: '4px' }} />
                                        </div>
                                    )}
                                    <input type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} style={{ display: 'none' }} disabled={isLoading} />
                                    <button type="button" className="btn btn-outline-primary me-2" onClick={() => fileInputRef.current?.click()} disabled={isLoading} >
                                        <FontAwesomeIcon icon={faImage} className="me-1" /> Choose File...
                                    </button>
                                    {imageFile && (
                                         <button type="button" className="btn btn-outline-danger ms-2" onClick={() => {
                                            setImageFile(null);
                                            if (imagePreviewUrl) URL.revokeObjectURL(imagePreviewUrl);
                                            setImagePreviewUrl(null);
                                            if(fileInputRef.current) fileInputRef.current.value = ''; // Reset file input
                                        }} disabled={isLoading} >
                                            Remove Photo
                                        </button>
                                    )}
                                </div>
                                <hr className="my-4"/>

                                <div className="d-grid">
                                    <button type="submit" className="btn btn-primary btn-lg" disabled={isLoading || !currentUserOrgId} >
                                        {isLoading ? (
                                            <>
                                                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                                                {uploadProgress > 0 && imageFile && message.type === 'info' ? `Uploading ${uploadProgress}%` : 'Processing...'}
                                            </>
                                        ) : (
                                            <>
                                                <FontAwesomeIcon icon={faUpload} className="me-1" /> Add Family
                                            </>
                                        )}
                                    </button>
                                    {!currentUserOrgId && !isLoading && (
                                        <small className="text-danger text-center mt-2">Cannot add family: Organization ID missing. Please re-login.</small>
                                    )}
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AddFamilyPage;